import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box, Typography, Stack, CircularProgress, Alert, Button, Chip, Divider,
} from '@mui/material';
import PrecisionManufacturingRounded from '@mui/icons-material/PrecisionManufacturingRounded';
import QrCodeScannerRounded from '@mui/icons-material/QrCodeScannerRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import RestartAltRounded from '@mui/icons-material/RestartAltRounded';
import {
  getInkSticker, InkStickerData, listInkTrolleyRuns, InkTrolleyRun,
  consumeInkUnits, InkOpenedTub,
} from './productionApi';
import { productionKeys } from './queryKeys';
import { useInkInvalidate } from './inkTrolley/useInkInvalidate';
import { TOKENS } from './board/ganttConfig';
import { useBarcodeScanner } from '../wms/useBarcodeScanner';
import ManualScanInput from '../wms/ManualScanInput';

/**
 * InkPressView — the press-side scan terminal (ink roadmap session 2, spec §6.3).
 *
 * Two scans, in order:
 *   1. The Job Bag's Code 128 job number. We look up the job (same data as the
 *      ink sticker) and the OPEN trolley run its ink was kitted onto.
 *   2. Each tub as it goes onto the press. That scan is the batch-to-job link —
 *      `POST /production/ink-units/consume` with the job, the press and the run.
 *      It is load-bearing, not confirmatory: a tub opened without this scan is
 *      still counted as used at settlement, but its supplier batch is tied to
 *      the run only, and the reconcile lists it as a gap.
 *
 * Same shape as the ink sticker terminal: HID scanner or typed input, no
 * FileMaker in the loop. A tub barcode looks like INK-000123-05; a job number
 * is a plain 6-digit token — the page tells them apart by the prefix, so the
 * order of scans is forgiving (a job scan mid-run simply switches job).
 */

// Who opened a tub is stamped from the SESSION backend-side (_actor in
// blueprints/ink_stock.py), not typed here — Jack, 11/09. The old
// 'ink-press.operator' localStorage key is dead; nothing reads it.

function apiErr(e: unknown): string {
  const x = e as { response?: { data?: { error?: string } }; message?: string };
  return x?.response?.data?.error ?? x?.message ?? 'Failed';
}
const isTubCode = (code: string) => /^INK-/i.test(code.trim());
const fmtKg = (v: number | null | undefined) =>
  (v == null ? '—' : `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`);
const fmtTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '—';

interface ScanRow {
  key: number;
  code: string;
  status: 'ok' | 'error';
  tub?: InkOpenedTub;
  warnings?: string[];
  error?: string;
  at: string;
}

export default function InkPressView() {
  const invalidate = useInkInvalidate();
  const [jobNumber, setJobNumber] = useState<string>('');
  const [runOverride, setRunOverride] = useState<number | null>(null);
  const [scans, setScans] = useState<ScanRow[]>([]);
  const [lastError, setLastError] = useState<string>('');

  // --- the job (same read as the ink sticker: what's on the bag) ------------
  const jobQ = useQuery({
    queryKey: productionKeys.inkSticker(jobNumber),
    queryFn: () => getInkSticker(jobNumber),
    enabled: !!jobNumber,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // --- the open run its ink was kitted onto --------------------------------
  const runsQ = useQuery({
    queryKey: ['production', 'ink-trolley-runs', 'open', 'job', jobNumber],
    queryFn: () => listInkTrolleyRuns('open', jobNumber),
    enabled: !!jobNumber,
    staleTime: 30 * 1000,
  });
  const runs = useMemo(() => runsQ.data ?? [], [runsQ.data]);
  const run: InkTrolleyRun | null = useMemo(() => {
    if (runs.length === 0) return null;
    if (runOverride != null) return runs.find((r) => r.id === runOverride) ?? runs[0];
    // Prefer the one already at the press — that's the trolley beside the operator.
    return runs.find((r) => r.status === 'at_press') ?? runs[0];
  }, [runs, runOverride]);
  const pressCode = run?.press_code ?? null;

  // --- opening a tub ---------------------------------------------------------
  const consume = useMutation({
    mutationFn: (code: string) => consumeInkUnits({
      unit_codes: [code],
      job_number: jobNumber,
      trolley_run_id: run?.id ?? null,
      press_code: pressCode,
    }),
    onSuccess: (res, code) => {
      const tub = res.opened[0];
      setScans((s) => [{
        key: Date.now(), code, status: 'ok', tub, warnings: res.warnings, at: new Date().toISOString(),
      }, ...s]);
      setLastError('');
      // Opening a tub moves stock AND changes what a run holds — the shared
      // hook knows the full list, including the demand roll-up that carries
      // allocated_kg (see useInkInvalidate).
      invalidate(run?.id);
    },
    onError: (e, code) => {
      const msg = apiErr(e);
      setScans((s) => [{ key: Date.now(), code, status: 'error', error: msg, at: new Date().toISOString() }, ...s]);
      setLastError(msg);
    },
  });

  const startJob = useCallback((code: string) => {
    setJobNumber(code);
    setRunOverride(null);
    setScans([]);
    setLastError('');
  }, []);

  const onScan = useCallback((raw: string) => {
    const code = (raw || '').trim();
    if (!code) return;
    if (isTubCode(code)) {
      if (!jobNumber) { setLastError(`Scan the Job Bag first — ${code.toUpperCase()} needs a job to go on.`); return; }
      consume.mutate(code.toUpperCase());
    } else {
      startJob(code);
    }
  }, [jobNumber, consume, startJob]);
  useBarcodeScanner({ onScan, minLength: 4 });

  const job: InkStickerData | undefined = jobQ.data;
  const okScans = scans.filter((s) => s.status === 'ok');
  const usedKg = okScans.reduce((s, r) => s + Number(r.tub?.nominal_kg ?? 0), 0);

  // Which inks the job wants on which unit — the checklist the operator is
  // working through, ticked as tubs of that ink get scanned.
  const scannedInks = useMemo(() => new Set(okScans.map((s) => s.tub?.ink_key).filter(Boolean)), [okScans]);
  const expectedUnits = (job?.units ?? []).filter((u) => u.name);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <PrecisionManufacturingRounded sx={{ color: TOKENS.accent }} />
        <Typography sx={{ fontSize: 20, fontWeight: 800 }}>Ink at press</Typography>
      </Stack>
      <Typography sx={{ fontSize: 13, color: TOKENS.ink3, mb: 2 }}>
        Scan the Job Bag, then scan each tub as it goes onto the press. That scan ties the tub’s
        supplier batch to the job — a tub opened without it is still counted as used, but nobody
        can say which job it went on.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        {/* ---- left: scanner + current job ---- */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Box sx={{ p: 2, borderRadius: '12px', border: `1px solid ${TOKENS.line}`, bgcolor: TOKENS.panel }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <QrCodeScannerRounded sx={{ color: TOKENS.accent, fontSize: 20 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {jobNumber ? 'Scan a tub' : 'Scan the Job Bag'}
              </Typography>
            </Stack>
            <ManualScanInput onSubmit={onScan}
              placeholder={jobNumber ? 'Scan or type a tub barcode, e.g. INK-000123-05' : 'Scan or type the job number…'} />
            {jobNumber && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                <Button size="small" startIcon={<RestartAltRounded />} onClick={() => startJob('')}
                  sx={{ color: TOKENS.ink3 }}>
                  Next job
                </Button>
              </Stack>
            )}
            {lastError && <Alert severity="error" sx={{ mt: 1.5 }}>{lastError}</Alert>}
          </Box>

          {jobNumber && (
            <Box sx={{ mt: 2, p: 2, borderRadius: '12px', border: `1px solid ${TOKENS.line}` }}>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
                <Typography sx={{ fontFamily: TOKENS.monoFont, fontSize: 22, fontWeight: 800 }}>{jobNumber}</Typography>
                {jobQ.isLoading && <CircularProgress size={14} />}
                {job?.customer_code && (
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: TOKENS.ink2 }}>{job.customer_code}</Typography>
                )}
              </Stack>
              {jobQ.isError && <Alert severity="warning" sx={{ mb: 1 }}>Couldn’t read the job: {apiErr(jobQ.error)}. Tubs can still be scanned onto it.</Alert>}
              {job && (
                <Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>
                  {job.description ?? '—'}
                  {job.forme_number ? ` · forme ${job.forme_number}` : ''}
                  {job.run_length_sheets ? ` · ${job.run_length_sheets.toLocaleString()} sheets` : ''}
                </Typography>
              )}

              <Divider sx={{ my: 1.25 }} />

              {/* the trolley run — where the ink was kitted */}
              {runsQ.isLoading ? (
                <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>Looking for the kitted trolley…</Typography>
              ) : run ? (
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={`${run.trolley_code} · run #${run.id}`}
                    sx={{ fontFamily: TOKENS.monoFont, fontWeight: 800, bgcolor: `${TOKENS.accent}1F`, color: TOKENS.accentStrong }} />
                  <Chip size="small" label={run.status === 'at_press' ? 'AT PRESS' : 'KITTED'}
                    sx={{ height: 20, fontSize: 10, fontWeight: 800, letterSpacing: 0.4, bgcolor: TOKENS.lineSoft, color: TOKENS.ink2 }} />
                  {pressCode && <Chip size="small" label={`Press ${pressCode}`} sx={{ height: 20, fontSize: 11, bgcolor: TOKENS.lineSoft }} />}
                  {run.family_run_id != null && (
                    <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3 }}>family run #{run.family_run_id}</Typography>
                  )}
                  {runs.length > 1 && (
                    <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
                      {runs.filter((r) => r.id !== run.id).map((r) => (
                        <Chip key={r.id} size="small" variant="outlined" label={`use ${r.trolley_code} #${r.id}`}
                          onClick={() => setRunOverride(r.id)} sx={{ height: 20, fontSize: 10.5, cursor: 'pointer' }} />
                      ))}
                    </Stack>
                  )}
                </Stack>
              ) : (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  No kitted trolley holds this job’s ink. Tubs scanned now are linked to the job only —
                  the ink room should kit a trolley for this run.
                </Alert>
              )}

              {/* what the job needs, ticked as it's scanned on */}
              {expectedUnits.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: TOKENS.ink3, mb: 0.5 }}>
                    Inks on the sticker
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {expectedUnits.map((u) => {
                      const done = !!u.ink_key && scannedInks.has(u.ink_key);
                      return (
                        <Chip key={u.unit} size="small"
                          icon={done ? <CheckCircleRounded sx={{ fontSize: 14 }} /> : undefined}
                          label={`U${u.unit} ${u.ink_key ?? u.name}`}
                          sx={{
                            height: 22, fontSize: 10.5, fontFamily: TOKENS.monoFont,
                            bgcolor: done ? `${TOKENS.good}1F` : TOKENS.lineSoft,
                            color: done ? TOKENS.good : (u.ink_key ? TOKENS.ink2 : TOKENS.warn),
                            '& .MuiChip-icon': { color: TOKENS.good },
                          }} />
                      );
                    })}
                  </Stack>
                  {(job?.unassigned ?? []).length > 0 && (
                    <Typography sx={{ fontSize: 11, color: TOKENS.warn, mt: 0.5 }}>
                      {job!.unassigned.length} separation{job!.unassigned.length === 1 ? '' : 's'} on this job
                      {' '}have no ink assigned yet — scan whatever tub is actually used.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* ---- right: what has gone on ---- */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Box sx={{ p: 2, borderRadius: '12px', border: `1px solid ${TOKENS.line}` }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                On the press{jobNumber ? ` · ${jobNumber}` : ''}
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: TOKENS.accent, fontVariantNumeric: 'tabular-nums' }}>
                {okScans.length} tub{okScans.length === 1 ? '' : 's'} · {fmtKg(usedKg)}
              </Typography>
            </Stack>
            {scans.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3 }}>
                Nothing scanned yet{jobNumber ? '' : ' — start with the Job Bag'}.
              </Typography>
            ) : (
              <Stack spacing={0.75}>
                {scans.map((s) => (
                  <Box key={s.key} sx={{
                    p: 1, borderRadius: '8px',
                    border: `1px solid ${s.status === 'ok' ? TOKENS.line : `${TOKENS.bad}66`}`,
                    bgcolor: s.status === 'ok' ? 'transparent' : `${TOKENS.bad}0D`,
                  }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {s.status === 'ok'
                        ? <CheckCircleRounded sx={{ fontSize: 16, color: TOKENS.good }} />
                        : <ErrorOutlineRounded sx={{ fontSize: 16, color: TOKENS.bad }} />}
                      <Typography sx={{ fontFamily: TOKENS.monoFont, fontWeight: 800, fontSize: 13 }}>{s.code}</Typography>
                      {s.tub && (
                        <Typography sx={{ fontFamily: TOKENS.monoFont, fontSize: 12.5, color: TOKENS.ink2 }}>
                          {s.tub.ink_key}
                        </Typography>
                      )}
                      <Box sx={{ flexGrow: 1 }} />
                      <Typography sx={{ fontSize: 11, color: TOKENS.ink3 }}>{fmtTime(s.at)}</Typography>
                    </Stack>
                    {s.tub && (
                      <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3, mt: 0.25, pl: 3 }}>
                        {s.tub.description ?? ''}
                        {' · '}batch {s.tub.batch_no ?? '—'}
                        {s.tub.supplier_batch_code ? ` / ${s.tub.supplier_batch_code}` : ''}
                        {' · '}{fmtKg(s.tub.nominal_kg)}
                        {s.tub.trolley_run_id != null ? ` · run #${s.tub.trolley_run_id}` : ' · no run'}
                      </Typography>
                    )}
                    {s.error && (
                      <Typography sx={{ fontSize: 11.5, color: TOKENS.bad, mt: 0.25, pl: 3 }}>{s.error}</Typography>
                    )}
                    {(s.warnings ?? []).map((w) => (
                      <Typography key={w} sx={{ fontSize: 11.5, color: TOKENS.warn, mt: 0.25, pl: 3 }}>{w}</Typography>
                    ))}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
